import axios from 'axios';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addFolderToConversation, removeFolderFromConversation } from '@/actions/folders';
import { displayNotification } from '@/actions/settings';
import { toggleCustomerTag } from '@/actions/tags';
import {
	locallyAddFolderToCustomerConversation,
	removeFolderFromCustomerConversation,
	toggleConversationOpen,
	toggleConversationPin,
	toggleMarkAsReadConversation,
	updateConversationAssignees,
	updateLoadedCustomerConversationTags,
} from '@/actions/webchat';
import { setCustomer, setStatus, setVisible } from '@/reducers/voiceReducer';
import { isCustomerOnActiveCall } from '@/utils/activeCustomerCall';
import getWebchatApiUrl from '../../../../utils/getWebchatUrl';

const getBusinessId = (conversation) => {
	const { _business } = conversation || {};
	return typeof _business === 'object' ? _business?._id : _business;
};

export const idsMatch = (a, b) => String(a || '') === String(b || '');

export const getFolderTitle = (folder) => folder?.title || folder?.name || 'Untitled folder';

export const getSelectedAssignees = (conversation) => {
	const { _assignees = [], _assignee, _department } = conversation || {};

	if (_department) {
		return [`DEPARTMENT_${_department}`];
	}

	if (_assignees?.length > 0) {
		return _assignees.map((_user) => `USER_${_user}`);
	}

	if (_assignee) {
		return [`USER_${_assignee}`];
	}

	return [];
};

// Read state as the row shows it: per-user when the account tracks read state per person.
export const isConversationReadForUser = (conversation, user) =>
	user?.hasOwnIsReadIndicators
		? (conversation?._readByUsers || []).some((_user) => idsMatch(_user, user?._id))
		: !!conversation?.isRead;

/**
 * The quick actions for one conversation in the list: read, open, pin, call,
 * assign, tags, folder and preferred channel. The desktop right-click menu and
 * the phone action sheet both run these, so they behave the same.
 */
export function useConversationQuickActions(conversation, { onShowVoiceUpsell } = {}) {
	const dispatch = useDispatch();
	const auth = useSelector((state) => state.auth);
	const departments = useSelector((state) => state.departments) || [];
	const folders = useSelector((state) => state.folders) || [];
	const tags = useSelector((state) => state.tags) || [];
	const users = useSelector((state) => state.users) || [];
	const voice = useSelector((state) => state.voice);

	const _business = getBusinessId(conversation);
	const customer = conversation?._customer;
	const currentUserId = auth.user?._id;

	const isRead = isConversationReadForUser(conversation, auth.user);
	const customerOnActiveCall = isCustomerOnActiveCall(voice?.ongoingCalls, customer);
	const isCallDisabled =
		customerOnActiveCall || ['connecting', 'ringing', 'answered'].includes(voice?.status);
	const hasVoiceProduct = auth.products?.includes('voice');
	const hasChannelChoice = customer?.phone && customer?.email;
	const preferredChannel = customer?.preferredChannel || 'Phone';
	const currentTags = (customer?.tags || []).filter(Boolean);
	const availableTags = useMemo(
		() => [...new Set([...tags, ...currentTags].filter(Boolean))],
		[tags, currentTags],
	);
	const isPinnedForMe = (conversation?._pinnedByUsers || []).some((_user) =>
		idsMatch(_user, currentUserId),
	);
	const isPinnedForBusiness = !!conversation?.pinnedForBusiness;

	const activeUsers = useMemo(
		() =>
			users.filter(
				(user) =>
					user.active &&
					(user._businesses || []).some((userBusiness) => idsMatch(userBusiness, _business)),
			),
		[users, _business],
	);

	const businessDepartments = useMemo(
		() => departments.filter((department) => idsMatch(department._business, _business)),
		[departments, _business],
	);

	const selectedAssignees = getSelectedAssignees(conversation);

	const notify = (payload) => dispatch(displayNotification(payload));

	const handleToggleRead = () => {
		dispatch(
			toggleMarkAsReadConversation({
				conversation,
				isRead: !isRead,
			}),
		);
	};

	const handleToggleOpen = () => {
		dispatch(
			toggleConversationOpen({
				conversation,
				open: !conversation.open,
			}),
		);
	};

	const handleTogglePin = async ({ scope, pinned }) => {
		const res = await dispatch(
			toggleConversationPin({
				conversation,
				scope,
				pinned,
			}),
		);

		if (res?.error) {
			notify({
				message: res.message || 'Error updating conversation pin',
				error: true,
			});
		}
	};

	const handleCallCustomer = () => {
		if (!customer?.phone || isCallDisabled) {
			return;
		}

		if (!hasVoiceProduct) {
			onShowVoiceUpsell?.();
			return;
		}

		dispatch(setCustomer(customer));
		dispatch(setStatus('connecting'));
		dispatch(setVisible(true));
	};

	const handleSelectAssignees = async (selectedValues) => {
		try {
			const departmentValue = selectedValues.find((value) => value.startsWith('DEPARTMENT_'));
			const isDepartment = !!departmentValue;
			const userIds = isDepartment
				? []
				: selectedValues
						.filter((value) => value.startsWith('USER_'))
						.map((value) => value.replace('USER_', ''));
			const _assignedUser = isDepartment
				? departmentValue.replace('DEPARTMENT_', '')
				: userIds.length
					? undefined
					: 'Unassigned';

			const payload = isDepartment
				? {
						isDepartment: true,
						_assignedUser,
						_customerConversation: conversation._id,
					}
				: {
						isDepartment: false,
						_assignees: userIds,
						_customerConversation: conversation._id,
					};

			await axios.post(
				`${getWebchatApiUrl(window.location.href)}/api/team/assign-conversation`,
				payload,
			);

			dispatch(
				updateConversationAssignees({
					_conversation: conversation._id,
					_assignees: userIds,
					_department: isDepartment ? _assignedUser : null,
				}),
			);
		} catch {
			notify({
				message: 'Error updating conversation assignee',
				error: true,
			});
		}
	};

	const handleToggleUserAssignee = (_user) => {
		const value = `USER_${_user}`;
		const selectedUsers = selectedAssignees.filter((assignee) => assignee.startsWith('USER_'));
		const nextSelectedUsers = selectedUsers.includes(value)
			? selectedUsers.filter((assignee) => assignee !== value)
			: [...selectedUsers, value];

		handleSelectAssignees(nextSelectedUsers);
	};

	const handleToggleDepartmentAssignee = (_department) => {
		const value = `DEPARTMENT_${_department}`;
		handleSelectAssignees(selectedAssignees.includes(value) ? [] : [value]);
	};

	const handleToggleCustomerTag = async (tag) => {
		const _customer = customer?._id;

		if (!_customer || !tag) {
			return;
		}

		const isAdding = !currentTags.includes(tag);
		const nextTags = isAdding
			? [...currentTags, tag]
			: currentTags.filter((currentTag) => currentTag !== tag);

		dispatch(
			updateLoadedCustomerConversationTags({
				_customer,
				tags: nextTags,
			}),
		);

		try {
			const { customer: updatedCustomer } = await dispatch(
				toggleCustomerTag({
					_customer,
					tag,
					isAdding,
					_business,
				}),
			);
			dispatch(
				updateLoadedCustomerConversationTags({
					_customer,
					tags: updatedCustomer.tags,
				}),
			);
		} catch {
			dispatch(
				updateLoadedCustomerConversationTags({
					_customer,
					tags: currentTags,
				}),
			);
			notify({
				message: 'Error updating customer tags',
				error: true,
			});
		}
	};

	const handleRemoveFolder = async (_folder) => {
		dispatch(
			removeFolderFromCustomerConversation({
				_conversation: conversation._id,
			}),
		);

		const res = await dispatch(
			removeFolderFromConversation({
				_conversation: conversation._id,
				_business,
			}),
		);

		if (!res || res.error) {
			notify({
				message: res?.message || 'Error removing conversation from folder',
				error: true,
			});
			dispatch(
				locallyAddFolderToCustomerConversation({
					_folder,
					_conversation: conversation._id,
				}),
			);
		}
	};

	const handleFolderAction = async (_folder) => {
		if (conversation._folder === _folder) {
			await handleRemoveFolder(_folder);
			return;
		}

		if (!folders.find((folder) => folder._id === _folder)) {
			return;
		}

		dispatch(
			locallyAddFolderToCustomerConversation({
				_folder,
				_conversation: conversation._id,
			}),
		);

		const res = await dispatch(
			addFolderToConversation({
				_folder,
				_conversation: conversation._id,
				_business,
			}),
		);

		if (!res || res.error) {
			notify({
				message: res?.message || 'Error adding conversation to folder',
				error: true,
			});
			dispatch(
				removeFolderFromCustomerConversation({
					_conversation: conversation._id,
				}),
			);
		}
	};

	const handleSelectPreferredChannel = async (channel) => {
		try {
			await axios.post(
				`${getWebchatApiUrl(window.location.href)}/api/conversation/set-preferred-channel`,
				{
					preferredChannel: channel,
					_customer: customer?._id,
					_business,
				},
			);
		} catch {
			notify({
				message: 'Error updating preferred channel',
				error: true,
			});
		}
	};

	return {
		auth,
		_business,
		customer,
		folders,
		isRead,
		customerOnActiveCall,
		isCallDisabled,
		hasChannelChoice,
		preferredChannel,
		currentTags,
		availableTags,
		isPinnedForMe,
		isPinnedForBusiness,
		activeUsers,
		businessDepartments,
		selectedAssignees,
		displayNotification: notify,
		toggleConversationPin: (args) => dispatch(toggleConversationPin(args)),
		handleToggleRead,
		handleToggleOpen,
		handleTogglePin,
		handleCallCustomer,
		handleSelectAssignees,
		handleToggleUserAssignee,
		handleToggleDepartmentAssignee,
		handleToggleCustomerTag,
		handleFolderAction,
		handleRemoveFolder,
		handleSelectPreferredChannel,
	};
}
